import { alpha, createTheme, darken } from '@mui/material';

// Paleta Clark Empilhadeiras (aproximação baseada no site institucional)
// Clark Green (Primária): #6CB33F
// Dark Neutral / Preto técnico: #231F20
// Cinzas de apoio: #4D4D4F, #7A7A7A, #E0E0E0, #F5F6F7
// Acentos de estado: Sucesso (variação verde), Erro (#D63031), Aviso (#F5A300), Info (#1E88E5)

const brand = {
  clarkGreen: '#6CB33F',
  clarkGreenHover: '#5CA034',
  dark: '#231F20',
  darkAlt: '#111111',
  gray600: '#4D4D4F',
  gray500: '#7A7A7A',
  gray300: '#E0E0E0',
  gray100: '#F5F6F7',
  white: '#FFFFFF',
  success: '#4E8E1F',
  warning: '#F5A300',
  error: '#D63031',
  info: '#1E88E5'
};

const themeColors = {
  primary: brand.clarkGreen,
  primaryAlt: brand.darkAlt,
  secondary: brand.dark,
  success: brand.success,
  warning: brand.warning,
  error: brand.error,
  info: brand.info,
  black: brand.dark,
  white: brand.white,
  trueWhite: brand.white
};

export const ClarkTheme = createTheme({
  general: {
    borderRadiusSm: '4px',
    borderRadius: '8px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px'
  },
  colors: {
    alpha: {
      white: {
        5: alpha(themeColors.white, 0.02),
        10: alpha(themeColors.white, 0.1),
        30: alpha(themeColors.white, 0.3),
        50: alpha(themeColors.white, 0.5),
        70: alpha(themeColors.white, 0.7),
        100: themeColors.white
      },
      trueWhite: {
        5: alpha(themeColors.trueWhite, 0.02),
        10: alpha(themeColors.trueWhite, 0.1),
        30: alpha(themeColors.trueWhite, 0.3),
        50: alpha(themeColors.trueWhite, 0.5),
        70: alpha(themeColors.trueWhite, 0.7),
        100: themeColors.trueWhite
      },
      black: {
        5: alpha(themeColors.black, 0.02),
        10: alpha(themeColors.black, 0.1),
        30: alpha(themeColors.black, 0.3),
        50: alpha(themeColors.black, 0.5),
        70: alpha(themeColors.black, 0.7),
        100: themeColors.black
      }
    },
    primary: {
      lighter: alpha(themeColors.primary, 0.15),
      light: alpha(themeColors.primary, 0.3),
      main: themeColors.primary,
      dark: darken(themeColors.primary, 0.18)
    },
    secondary: {
      lighter: alpha(themeColors.secondary, 0.15),
      light: alpha(themeColors.secondary, 0.3),
      main: themeColors.secondary,
      dark: darken(themeColors.secondary, 0.3)
    },
    success: {
      lighter: alpha(themeColors.success, 0.15),
      light: alpha(themeColors.success, 0.3),
      main: themeColors.success,
      dark: darken(themeColors.success, 0.2)
    },
    warning: {
      lighter: alpha(themeColors.warning, 0.18),
      light: alpha(themeColors.warning, 0.32),
      main: themeColors.warning,
      dark: darken(themeColors.warning, 0.18)
    },
    error: {
      lighter: alpha(themeColors.error, 0.18),
      light: alpha(themeColors.error, 0.32),
      main: themeColors.error,
      dark: darken(themeColors.error, 0.18)
    },
    info: {
      lighter: alpha(themeColors.info, 0.15),
      light: alpha(themeColors.info, 0.3),
      main: themeColors.info,
      dark: darken(themeColors.info, 0.18)
    }
  },
  palette: {
    mode: 'light',
    primary: {
      light: alpha(themeColors.primary, 0.3),
      main: themeColors.primary,
      dark: darken(themeColors.primary, 0.18),
      contrastText: brand.white
    },
    secondary: {
      light: alpha(themeColors.secondary, 0.3),
      main: themeColors.secondary,
      dark: darken(themeColors.secondary, 0.25),
      contrastText: brand.white
    },
    success: {
      light: alpha(themeColors.success, 0.35),
      main: themeColors.success,
      dark: darken(themeColors.success, 0.25),
      contrastText: brand.white
    },
    warning: {
      light: alpha(themeColors.warning, 0.4),
      main: themeColors.warning,
      dark: darken(themeColors.warning, 0.25),
      contrastText: brand.dark
    },
    error: {
      light: alpha(themeColors.error, 0.35),
      main: themeColors.error,
      dark: darken(themeColors.error, 0.25),
      contrastText: brand.white
    },
    info: {
      light: alpha(themeColors.info, 0.35),
      main: themeColors.info,
      dark: darken(themeColors.info, 0.25),
      contrastText: brand.white
    },
    text: {
      primary: brand.dark,
      secondary: brand.gray600,
      disabled: alpha(brand.dark, 0.4)
    },
    background: {
      default: brand.gray100,
      paper: brand.white
    },
    divider: brand.gray300,
    action: {
      hover: alpha(themeColors.primary, 0.08),
      selected: alpha(themeColors.primary, 0.14),
      disabled: alpha(brand.dark, 0.3),
      disabledBackground: alpha(brand.dark, 0.08),
      focus: alpha(themeColors.primary, 0.2)
    }
  },
  shape: {
    borderRadius: 8
  },
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
    h1: { fontWeight: 600, fontSize: '2rem', color: brand.dark },
    h2: { fontWeight: 600, fontSize: '1.5rem', color: brand.dark },
    h5: { fontWeight: 500, fontSize: '1.25rem', color: brand.dark },
    h6: { fontWeight: 600, fontSize: '1.1rem', color: brand.dark },
    body1: { fontSize: '1rem', color: brand.gray600 },
    body2: { fontSize: '0.875rem', color: brand.gray500 }
  },
  sidebar: {
    width: '290px',
    boxShadow: '1px 0 0 ' + alpha(themeColors.black, 0.15)
  },
  header: {
    height: '80px',
    background: themeColors.primaryAlt,
    textColor: themeColors.white
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--primary': brand.clarkGreen,
          '--primary-hover': brand.clarkGreenHover,
          '--primary-light': 'rgba(108, 179, 63, 0.15)',
          '--primary-shadow': '0 2px 8px rgba(108, 179, 63, 0.30)',
          '--secondary': brand.dark,
          '--secondary-alt': brand.darkAlt,
          '--secondary-contrast': brand.white,
          '--success': brand.success,
          '--success-light': 'rgba(78, 142, 31, 0.15)',
          '--success-hover': '#41781a',
          '--warning': brand.warning,
          '--warning-light': 'rgba(245, 163, 0, 0.18)',
          '--warning-hover': '#d48f00',
          '--error': brand.error,
          '--error-light': 'rgba(214, 48, 49, 0.18)',
          '--error-hover': '#b71c1c',
          '--info': brand.info,
          '--info-light': 'rgba(30, 136, 229, 0.18)',
          '--info-hover': '#1565c0',
          '--neutral-900': brand.dark,
          '--neutral-800': brand.gray600,
          '--neutral-600': brand.gray500,
          '--neutral-300': brand.gray300,
          '--neutral-100': brand.gray100,
          '--neutral-000': brand.white,
          '--text-primary': 'var(--neutral-900)',
          '--text-secondary': 'var(--neutral-800)',
          '--text-muted': 'var(--neutral-600)',
          '--background': 'var(--neutral-100)',
          '--background-alt': 'var(--neutral-000)',
          '--background-card': 'var(--neutral-000)',
          '--background-sidebar': brand.darkAlt,
          '--background-header': brand.darkAlt,
          '--border': 'var(--neutral-300)',
          '--border-hover': 'var(--primary)',
          '--border-focus': 'var(--primary)',
          '--spacing-xs': '0.5rem',
          '--spacing-sm': '1rem',
          '--spacing-md': '1.5rem',
          '--spacing-lg': '2rem',
          '--spacing-xl': '3rem'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500
        },
        containedPrimary: {
          boxShadow: '0 2px 8px rgba(108,179,63,0.3)',
          '&:hover': {
            backgroundColor: brand.clarkGreenHover,
            boxShadow: '0 4px 14px rgba(108,179,63,0.35)'
          }
        },
        outlinedPrimary: {
          borderWidth: 1.5,
          '&:hover': {
            backgroundColor: alpha(themeColors.primary, 0.08)
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${brand.gray300}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined'
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: brand.white,
          '& fieldset': { borderColor: brand.gray300 },
          '&:hover fieldset': { borderColor: themeColors.primary },
          '&.Mui-focused fieldset': { borderColor: themeColors.primary }
        }
      }
    }
  }
});
